import { DocumentAnalysisResponse } from '../types/document';
import { ImageApiConfigError, imageApiHeaders, imageApiUrl } from './imageApi';

// SAM-949: these calls go through the authenticated image API (services/imageApi.ts): the signed-in user's Bearer and
// X-Api-Key on `${VITE_API_BASE_URL}/ocrservice/image`. There is no public key and no default URL; without the base URL
// or a session they send nothing. The public registration pages use api/publicApi instead.

/**
 * Maximum raw image size accepted by the document analyzer, in bytes.
 *
 * ASSUMPTION (SAM-252): no documented/configurable limit was found for this
 * service — it is a single Lambda behind API Gateway that receives the image
 * as a base64 string in the JSON body (see `analyzeDocument` below). API
 * Gateway REST APIs hard-cap the request payload at 10 MB, and base64 encoding
 * inflates the raw file size by ~37%. 7 MB of raw image data encodes to
 * ~9.6 MB, which stays safely under that ceiling. If the real service limit
 * is ever documented or made configurable (e.g. via an env var), update this
 * single constant rather than the limit's callers.
 */
export const MAX_ANALYZER_IMAGE_BYTES = 7 * 1024 * 1024; // 7 MB

/** File extensions accepted by the document analyzer upload control. */
export const ACCEPTED_ANALYZER_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

/** MIME types accepted by the document analyzer upload control. */
export const ACCEPTED_ANALYZER_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];

interface AnalyzeDocumentParams {
  image: string;
  filename: string;
}

export const analyzeDocument = async (params: AnalyzeDocumentParams): Promise<DocumentAnalysisResponse> => {
  try {
    const response = await fetch(imageApiUrl('action=analyze&savetype=base64'), {
      method: 'POST',
      headers: imageApiHeaders({ json: true }),
      body: JSON.stringify({
        image: params.image,
        filename: params.filename
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // If the response includes a statusCode and body, extract the inner data
    if ('statusCode' in data && 'body' in data) {
      return data.body as DocumentAnalysisResponse;
    }
    
    // Otherwise return the data directly
    return data as DocumentAnalysisResponse;
  } catch (error) {
    console.error('Error analyzing document:', error);
    throw error;
  }
};

/** The face crop could not be produced (service, network or configuration), as opposed to a photo with no face. */
export class ProfilePictureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProfilePictureError';
  }
}

/**
 * Generate a profile picture by extracting the face from a photo with Rekognition.
 *
 * It goes through the SAME authenticated API as the upload (`${VITE_API_BASE_URL}/ocrservice/image`, the caller's
 * Bearer and X-Api-Key), so the environment is the one the photo was uploaded to and savePicture knows who asks. No
 * public key and no default URL: without the base URL or a session it fails closed (Logic, 15 Oct 2026).
 *
 * @param photoUrl - The S3 key of the photo (e.g. "prod/abc123.jpg")
 * @returns The S3 key of the cropped face, `cropped_faces/{photoUrl}_face_0.jpg`, or null when the photo has no face
 * @throws ProfilePictureError when the crop could not be requested or the service failed
 */
export const generateProfilePicture = async (photoUrl: string): Promise<string | null> => {
  let response: Response;
  try {
    response = await fetch(imageApiUrl('action=generateProfilePicture'), {
      method: 'POST',
      headers: imageApiHeaders({ json: true }),
      body: JSON.stringify({ photo_url: photoUrl }),
    });
  } catch (error) {
    // No base URL or no session (ImageApiConfigError) and an unreachable service are the same to the caller.
    console.error('Error generating profile picture:', error);
    throw new ProfilePictureError(error instanceof ImageApiConfigError ? error.message : 'The profile picture service could not be reached');
  }
  // savePicture answers 404 «No face detected» when the photo has none.
  if (response.status === 404) return null;
  if (!response.ok) {
    console.warn('generateProfilePicture failed:', response.status);
    throw new ProfilePictureError(`The profile picture service answered ${response.status}`);
  }
  const data = await response.json();
  return data.profile_picture || null;
};

/**
 * Re-analyze an existing S3 document image to extract OCR fields.
 * @param photoUrl - The S3 key of the document photo
 * @returns The extracted document data or null if failed
 */
export const reAnalyzeFromS3 = async (photoUrl: string): Promise<{ documentType: string; data: Record<string, any> } | null> => {
  try {
    const response = await fetch(imageApiUrl('action=reAnalyzeFromS3'), {
      method: 'POST',
      headers: imageApiHeaders({ json: true }),
      body: JSON.stringify({ photo_url: photoUrl }),
    });

    if (!response.ok) {
      console.warn('reAnalyzeFromS3 failed:', response.status);
      return null;
    }

    const result = await response.json();
    return result.data ? { documentType: result.documentType, data: result.data } : null;
  } catch (error) {
    console.error('Error re-analyzing document:', error);
    return null;
  }
}; 